import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { ETSY_SHOP_URL, ETSY_BACH_URL, goToEtsy } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

function Loader() {
  return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin" /></div>;
}

// Mints a read-only demo token for the given product, then renders the real planner.
export function DemoBoot({ product, children }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let done = false;
    track(product === "bachelorette_planner" ? "bachelorette_demo_opened" : "wedding_demo_opened", product);
    (async () => {
      try {
        const { data } = await api.post("/demo/start", { product });
        sessionStorage.setItem("pmw_demo", data.token);
        sessionStorage.setItem("pmw_demo_product", product);
      } catch { /* demo endpoint is public; ignore */ }
      if (!done) setReady(true);
    })();
    return () => { done = true; };
  }, [product]);
  if (!ready) return <Loader />;
  return children;
}

const purchaseLink = (product) => {
  track("demo_purchase_clicked", product);
  if (product === "bachelorette_planner" && ETSY_BACH_URL) { window.open(ETSY_BACH_URL, "_blank", "noopener,noreferrer"); return; }
  if (ETSY_SHOP_URL) goToEtsy(); else window.location.assign("/#choose");
};

export function DemoChrome({ product }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const onLock = () => { setOpen(true); track("demo_locked_action_clicked", product); };
    window.addEventListener("demo-locked", onLock);
    return () => window.removeEventListener("demo-locked", onLock);
  }, [product]);
  const label = product === "bachelorette_planner" ? "Bachelorette Planner" : "Wedding Planner";

  return (
    <>
      {/* Persistent, non-obstructive demo banner */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 rounded-full border border-border bg-background/95 backdrop-blur px-4 py-2 shadow-lg max-w-[94vw]" data-testid="demo-banner">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary whitespace-nowrap">
          <Sparkles className="h-3.5 w-3.5 text-accent" /> DEMO MODE
        </span>
        <span className="hidden sm:inline text-xs text-muted-foreground">Explore freely — changes are disabled.</span>
        <Button size="sm" onClick={() => purchaseLink(product)} className="rounded-full h-8 text-xs" data-testid="demo-get-access">
          Get Full Access <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="demo-lock-modal">
          <DialogHeader>
            <div className="mx-auto h-11 w-11 rounded-full bg-secondary flex items-center justify-center mb-2"><Lock className="h-5 w-5 text-primary" /></div>
            <DialogTitle className="text-center font-serif text-2xl text-primary">Ready to start your own plan? 🤍</DialogTitle>
            <DialogDescription className="text-center">
              You're exploring the PlanMyWedding {label} demo. Get access to create, edit and save your own planner.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button onClick={() => purchaseLink(product)} className="rounded-full w-full" data-testid="demo-modal-get">Get Access</Button>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full w-full" data-testid="demo-modal-continue">Continue Exploring</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
